import DOMPurify from "dompurify";
import {
  Activity,
  AlertCircle,
  Bug,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  Folder,
  FolderPlus,
  Info,
  Link2,
  ListChecks,
  Loader2,
  Search,
  ShieldCheck,
  User,
  X,
  Zap,
} from "lucide-react";
import { marked } from "marked";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  PriorityBadge,
  TestCaseStatusBadge,
  TestTypeBadge,
  CycleStatusBadge,
  RequirementStatusBadge,
  DefectStatusBadge,
  SeverityBadge,
} from "@/common/components/badge/StatusBadge";
import TestStepResultParser from "@/common/components/test-steps/TestStepResultParser";
import { useAuth } from "@/common/hooks/useAuth";
import { usePermissions } from "@/common/hooks/usePermissions";
import { useToast } from "@/common/hooks/useToast";
import { formatShortDateTime } from "@/common/utils/date";
import {
  useCycles,
  useCycleById,
} from "@/common/services/cycles/cycles.queries";
import {
  useAllDefects,
  useDefectById,
  useProjectDefects,
} from "@/common/services/defectlogs/defectlogs.queries";
import {
  useReleaseFolders,
  useCreateReleaseFolder,
} from "@/common/services/release-folders";
import {
  useAllRequirements,
  useRequirementById,
} from "@/common/services/requirement";
import {
  usePriorities,
  useAllTestCases,
  useFetchFolderList,
  useTestCaseDetailsQuery,
  useTestStepsQuery,
} from "@/common/services/scenarios";
import {
  useNaturalTextActionsQuery,
  useTeamMembers,
} from "@/common/services/scenarios/scenarios.queries";
import type {
  Cycle,
  CycleSummary,
  ReleaseFolderTree,
} from "@/common/types/Cycle";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CreateCycleDialog } from "@/pages/cycles/components/create-cycle-dialog";
import { TestCaseTooltip } from "@/pages/scenarios/all-scenarios/folder/components/TestCaseTooltip";
import {
  useFolderTree,
  type TreeNode,
} from "@/pages/scenarios/all-scenarios/folder/hooks/useFolderTree";
import { ReqTypeBadge } from "@/pages/scenarios/requirements/list/requirementAtoms";

export type LinkEntityMode = "testcase" | "requirement" | "cycle" | "defect";

interface EntityLinkDialogProps {
  open: boolean;
  onClose: () => void;
  /** Which entity is being picked. */
  mode: LinkEntityMode;
  /** Workspace version to scope folders + entities. */
  versionId?: number;
  /** Hide this id from the list (e.g. a requirement can't link to itself). */
  excludeId?: number;
  /** Ids that are already linked — shown pre-checked + disabled. */
  alreadyLinkedIds?: number[];
  /** Specific step links already existing on the entity */
  alreadyLinkedSteps?: Array<{
    id?: number;
    testCaseId: number;
    testStepId: number | null;
  }>;
  /**
   * Called with the newly-selected ids (excludes already-linked). The matching
   * row objects are passed as a second argument so callers that need entity
   * metadata (name/type) don't have to re-fetch.
   */
  onConfirm: (ids: number[], rows?: EntityRow[]) => Promise<void> | void;
  /**
   * Optional unlink handler. When provided, already-linked rows become
   * un-checkable: un-ticking one marks it for removal and these ids are passed
   * here on confirm. When omitted, linked rows stay checked + disabled.
   */
  onUnlink?: (ids: number[]) => Promise<void> | void;
  /**
   * Optional step unlink handler. When provided, already-linked steps can be
   * un-checked to remove individual step links on confirm.
   */
  onUnlinkSteps?: (stepLinkIds: number[]) => Promise<void> | void;
  /**
   * Cycle mode only: the test cases this action will add to the picked
   * cycle(s), shown as a persistent chip strip in the center pane so the
   * selection stays visible even while browsing/creating release folders
   * (issue: selected test cases "disappeared" while picking a cycle folder).
   */
  contextEntities?: any[];
  /**
   * Cycle mode only: test case type (AUTOMATION or MANUAL) to filter cycles.
   * Only cycles matching this type will be shown.
   */
  testCaseType?: "AUTOMATION" | "MANUAL";
}

interface EntityRow {
  id: number;
  name: string;
  code: string;
  /** raw type/script descriptor for the inline badge */
  typeValue?: string;
  /** cycle status (DRAFT/ACTIVE/CLOSED) for the inline badge */
  statusValue?: string;
  /** cycle case count */
  caseCount?: number;
}

const MODE_CONFIG: Record<
  LinkEntityMode,
  {
    title: string;
    subtitle: string;
    icon: typeof ListChecks;
    codePrefix: string;
    entityWord: string;
    actionVerb: string;
    folderType?: string;
    folderCountType?: string;
  }
> = {
  testcase: {
    title: "Link test cases",
    subtitle: "Select test cases to link.",
    icon: ListChecks,
    codePrefix: "TC",
    entityWord: "test case",
    actionVerb: "Link",
    folderType: "TESTCASE",
    folderCountType: undefined,
  },
  requirement: {
    title: "Link requirements",
    subtitle: "Select requirements to link.",
    icon: Link2,
    codePrefix: "REQ",
    entityWord: "requirement",
    actionVerb: "Link",
    folderType: "REQUIREMENT",
    folderCountType: "requirement",
  },
  cycle: {
    title: "Add to test cycle",
    subtitle: "Select the cycles to add these test cases to.",
    icon: ClipboardList,
    codePrefix: "CYC",
    entityWord: "cycle",
    actionVerb: "Add to",
  },
  defect: {
    title: "Link defects",
    subtitle: "Select defects to link.",
    icon: Bug,
    codePrefix: "DEF",
    entityWord: "defect",
    actionVerb: "Link",
  },
};

const ALL_CYCLES_NODE_ID = 0;
const ALL_DEFECTS_NODE_ID = 0;
const OPEN_DEFECTS_NODE_ID = 1;
const IN_PROGRESS_DEFECTS_NODE_ID = 2;
const FIXED_DEFECTS_NODE_ID = 3;
const CLOSED_DEFECTS_NODE_ID = 4;

/* ── helpers ─────────────────────────────────────────────────────── */
function stripHtml(raw?: string): string {
  if (!raw) return "";
  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function parseCriteria(raw?: string): string[] {
  if (!raw || !raw.trim()) return [];
  const withBreaks = raw
    .replace(/<\/(li|p|div|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  const text = stripHtml(withBreaks);
  return text
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, "").trim())
    .filter(Boolean);
}

function getStepLabel(s: any, index: number): string {
  if (!s) return `Step #${index + 1}`;
  if (s.name && typeof s.name === "string" && s.name.trim()) return s.name.trim();
  if (s.action && typeof s.action === "string" && s.action.trim()) return stripHtml(s.action).trim();
  if (s.manualTestSteps && typeof s.manualTestSteps === "string" && s.manualTestSteps.trim()) return stripHtml(s.manualTestSteps).trim();
  return `Step #${index + 1}`;
}

function SectionLabel({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-muted-foreground" />
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </span>
    </div>
  );
}

/* ── skeleton loaders (match the real layout structure) ──────────── */
function FolderSkeleton() {
  return (
    <div className="space-y-1.5 px-3 py-1">
      {[88, 70, 60, 78, 64].map((w, i) => (
        <div key={i} className="flex items-center gap-1.5 py-1">
          <Skeleton className="h-3.5 w-3.5 rounded" />
          <Skeleton className="h-3.5 w-3.5 rounded" />
          <Skeleton className="h-3 rounded" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div>
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2.5 border-b border-border px-3 py-2"
        >
          <Skeleton className="h-4 w-4 shrink-0 rounded" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-2.5 w-10 rounded" />
              <Skeleton className="h-3.5 w-16 rounded-full" />
            </div>
            <Skeleton
              className="h-3 rounded"
              style={{ width: `${55 + ((i * 9) % 35)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div>
      <div className="border-b border-border px-5 py-4">
        <Skeleton className="h-2.5 w-12 rounded" />
        <Skeleton className="mt-2 h-4 w-3/4 rounded" />
        <div className="mt-3 flex gap-1.5">
          <Skeleton className="h-4 w-14 rounded-full" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
      </div>
      {[0, 1].map((s) => (
        <div key={s} className="border-b border-border px-5 py-4">
          <Skeleton className="h-2.5 w-20 rounded" />
          <Skeleton className="mt-2 h-3 w-full rounded" />
          <Skeleton className="mt-1.5 h-3 w-5/6 rounded" />
        </div>
      ))}
    </div>
  );
}

export function EntityLinkDialog({
  open,
  onClose,
  mode,
  versionId: versionIdProp,
  excludeId,
  alreadyLinkedIds = [],
  alreadyLinkedSteps = [],
  onConfirm,
  onUnlink,
  onUnlinkSteps,
  contextEntities,
  testCaseType,
}: EntityLinkDialogProps) {
  const cfg = MODE_CONFIG[mode];
  const { showToast } = useToast();
  const { userPreferenceData } = useAuth();
  const versionId =
    versionIdProp ??
    (userPreferenceData?.data?.versionId as number | undefined);

  const [search, setSearch] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  /** Already-linked ids the user has un-ticked (to remove on confirm). */
  const [unlinkSet, setUnlinkSet] = useState<Set<number>>(new Set());
  /** Already-linked steps the user has un-ticked. Key: `${testCaseId}_${testStepId}` */
  const [unlinkStepSet, setUnlinkStepSet] = useState<Set<string>>(new Set());
  /** Map of testCaseId -> Map of stepId -> stepName for linking specific steps */
  const [selectedStepsByTc, setSelectedStepsByTc] = useState<
    Map<number, Map<number, string>>
  >(new Map());
  const [focusedId, setFocusedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Resizable preview pane (drag the divider between center and right).
  const [previewWidth, setPreviewWidth] = useState(420);
  const resizingRef = useRef(false);
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      // Width grows as the cursor moves left; clamp between 320–620px.
      setPreviewWidth((prev) => {
        const next = prev - e.movementX;
        return Math.min(620, Math.max(320, next));
      });
    };
    const onUp = () => {
      resizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const alreadyLinked = useMemo(
    () => new Set(alreadyLinkedIds.map(Number)),
    [alreadyLinkedIds],
  );

  const alreadyLinkedStepsMap = useMemo(() => {
    const map = new Map<number, Set<number>>();
    (alreadyLinkedSteps ?? []).forEach((item) => {
      if (item.testStepId != null) {
        if (!map.has(item.testCaseId)) {
          map.set(item.testCaseId, new Set());
        }
        map.get(item.testCaseId)!.add(Number(item.testStepId));
      }
    });
    return map;
  }, [alreadyLinkedSteps]);

  const alreadyLinkedStepLinkIds = useMemo(() => {
    const map = new Map<string, number>();
    (alreadyLinkedSteps ?? []).forEach((item) => {
      if (item.testStepId != null && item.id != null) {
        map.set(`${item.testCaseId}_${item.testStepId}`, Number(item.id));
      }
    });
    return map;
  }, [alreadyLinkedSteps]);

  // ── Folder tree ───────────────────────────────────────────────
  // Test-case / requirement folders come from the shared folder table (lazy).
  // Cycle mode uses release folders instead (fetched fully-nested), so the
  // test/req folder query is disabled by withholding the versionId.
  const { data: rootFolders, isLoading: foldersLoading } = useFetchFolderList(
    mode === "cycle" || mode === "defect" ? undefined : versionId,
    cfg.folderType,
    cfg.folderCountType,
  );
  const {
    folderTree,
    initializeTree,
    toggleCollapse,
    isNodeCollapsed,
    isNodeLoading,
    setFolderTree,
  } = useFolderTree();

  // Release folders (cycle mode only) — already nested with children.
  const { data: releaseFolders, isLoading: releaseFoldersLoading } =
    useReleaseFolders(mode === "cycle" ? (versionId ?? null) : null);

  const hasInitialized = useRef(false);

  // Reset everything whenever the dialog opens / version changes.
  useEffect(() => {
    hasInitialized.current = false;
    initializeTree([]);
    setSelectedFolderId(null);
    setSelected(new Set());
    setUnlinkSet(new Set());
    setUnlinkStepSet(new Set());
    setSelectedStepsByTc(new Map());
    setFocusedId(null);
    setSearch("");
  }, [open, versionId, initializeTree]);

  // ── "New folder" inline creation (cycle mode only — organises cycles into
  //    release folders, mirrors the same affordance on the main Cycles page). ──
  const { can } = usePermissions();
  const canCreateReleaseFolder = can("TEST_CYCLE", "WRITE");
  const { mutateAsync: createReleaseFolderMutation } = useCreateReleaseFolder();
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isSavingFolder, setIsSavingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCreatingFolder)
      setTimeout(() => newFolderInputRef.current?.focus(), 0);
  }, [isCreatingFolder]);

  useEffect(() => {
    // Collapse the inline creation row whenever the dialog closes/reopens.
    if (!open) {
      setIsCreatingFolder(false);
      setNewFolderName("");
    }
  }, [open]);

  const startCreateFolder = () => {
    setNewFolderName("");
    setIsCreatingFolder(true);
  };
  const cancelCreateFolder = () => {
    setNewFolderName("");
    setIsCreatingFolder(false);
  };
  const saveNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      cancelCreateFolder();
      return;
    }
    if (versionId == null) {
      showToast({ title: "No workspace version selected", variant: "error" });
      return;
    }
    setIsSavingFolder(true);
    try {
      const created = await createReleaseFolderMutation({
        name,
        workspace_version_id: versionId,
      });
      if (created?.id != null) {
        // Append directly to the tree + auto-select it, rather than relying on
        // the one-shot `hasInitialized` build effect (it only runs once per
        // dialog open and won't re-run just because the query refetched).
        setFolderTree((prev) => [
          ...prev,
          {
            id: Number(created.id),
            title: created.name,
            hasChildren: false,
            children: [],
            isLoaded: true,
          },
        ]);
        setSelectedFolderId(Number(created.id));
      }
      showToast({ title: "Release folder created", variant: "success" });
      cancelCreateFolder();
    } catch {
      showToast({ title: "Failed to create release folder", variant: "error" });
    } finally {
      setIsSavingFolder(false);
    }
  };

  // ── "New cycle" inline creation (cycle mode only — lets the user create a
  //    cycle right inside an empty release folder instead of leaving the
  //    dialog to use the Cycles page). ──
  const canCreateCycle = can("TEST_CYCLE", "WRITE");
  const [isCreateCycleDialogOpen, setIsCreateCycleDialogOpen] = useState(false);

  const startCreateCycle = () => setIsCreateCycleDialogOpen(true);

  const handleCycleCreated = (created: Cycle) => {
    const createdFolderId = created.release_folder_id ?? ALL_CYCLES_NODE_ID;
    setSelectedFolderId(createdFolderId);
    setSelected((prev) => new Set(prev).add(Number(created.id)));
    setFocusedId(Number(created.id));
  };

  // Test-case / requirement folder init (lazy folder table).
  useEffect(() => {
    if (mode === "cycle" || mode === "defect") return;
    if (!open || hasInitialized.current) return;
    if (foldersLoading || rootFolders === undefined) return;
    const folders = (rootFolders as any)?.data ?? [];
    const rootOnly = folders.filter((f: any) => f.parent_id == null);
    const formatted = initializeTree(rootOnly);
    if (formatted.length > 0) setSelectedFolderId(formatted[0].id);
    hasInitialized.current = true;
  }, [open, mode, rootFolders, foldersLoading, initializeTree]);

  // Cycle folder init — build a static tree from the nested release folders and
  // prepend a synthetic "All cycles" node (selected by default).
  useEffect(() => {
    if (mode !== "cycle") return;
    if (!open || hasInitialized.current) return;
    if (releaseFoldersLoading || releaseFolders === undefined) return;
    const toNode = (f: ReleaseFolderTree): TreeNode => {
      const children = (f.children ?? []).map(toNode);
      return {
        id: Number(f.id),
        title: f.name,
        hasChildren: children.length > 0,
        children,
        isLoaded: true,
      };
    };
    const releaseNodes = (releaseFolders ?? []).map(toNode);
    const tree: TreeNode[] = [
      {
        id: ALL_CYCLES_NODE_ID,
        title: "All cycles",
        hasChildren: false,
        children: [],
        isLoaded: true,
      },
      ...releaseNodes,
    ];
    setFolderTree(tree);
    setSelectedFolderId(ALL_CYCLES_NODE_ID);
    hasInitialized.current = true;
  }, [open, mode, releaseFolders, releaseFoldersLoading, setFolderTree]);

  // Defect status tree init — static status nodes: All defects, Open, In progress, Fixed, Closed
  useEffect(() => {
    if (mode !== "defect") return;
    if (!open || hasInitialized.current) return;
    const tree: TreeNode[] = [
      {
        id: ALL_DEFECTS_NODE_ID,
        title: "All defects",
        hasChildren: false,
        children: [],
        isLoaded: true,
      },
      {
        id: OPEN_DEFECTS_NODE_ID,
        title: "Open",
        hasChildren: false,
        children: [],
        isLoaded: true,
      },
      {
        id: IN_PROGRESS_DEFECTS_NODE_ID,
        title: "In progress",
        hasChildren: false,
        children: [],
        isLoaded: true,
      },
      {
        id: FIXED_DEFECTS_NODE_ID,
        title: "Fixed",
        hasChildren: false,
        children: [],
        isLoaded: true,
      },
      {
        id: CLOSED_DEFECTS_NODE_ID,
        title: "Closed",
        hasChildren: false,
        children: [],
        isLoaded: true,
      },
    ];
    setFolderTree(tree);
    setSelectedFolderId(ALL_DEFECTS_NODE_ID);
    hasInitialized.current = true;
  }, [open, mode, setFolderTree]);

  // ── Entities for the selected folder ──────────────────────────
  // Only the active mode's query is enabled — gate the inactive one by passing
  // an undefined/0 versionId (both hooks key `enabled` off versionId).
  const { data: tcRes, isLoading: tcLoading } = useAllTestCases({
    versionId: mode === "testcase" ? versionId! : (undefined as any),
    testCaseFolderId: selectedFolderId ?? undefined,
    query: search,
    size: 200,
  } as any);

  const { data: reqRes, isLoading: reqLoading } = useAllRequirements({
    versionId: mode === "requirement" ? (versionId ?? 0) : 0,
    folderId: selectedFolderId ?? undefined,
    size: 200,
    recursive: true,
  } as any);

  const { data: cyclesRes, isLoading: cyclesLoading } = useCycles(
    { workspaceVersionId: versionId, size: 200, sort: "id,desc" } as any,
    open && mode === "cycle" && !!versionId,
  );

  const { data: defectsRes, isLoading: defectsLoading } = useAllDefects(
    versionId ?? 0,
    { enabled: open && mode === "defect" && !!versionId },
  );

  const { data: projectDefectsRes, isLoading: projectDefectsLoading } =
    useProjectDefects({
      enabled: open && mode === "defect" && !versionId,
    });

  const defectsList = useMemo(() => {
    if (mode !== "defect") return [];
    const activeRes = versionId ? defectsRes : projectDefectsRes;
    const pages = (activeRes as any)?.pages ?? [];
    return pages.flatMap((p: any) => p?.data?.content ?? []);
  }, [mode, versionId, defectsRes, projectDefectsRes]);

  const cyclesById = useMemo(() => {
    const map = new Map<number, CycleSummary>();
    if (mode === "cycle") {
      for (const c of (cyclesRes as any)?.content ?? [])
        map.set(Number(c.id), c);
    }
    return map;
  }, [mode, cyclesRes]);

  const listLoading =
    mode === "testcase"
      ? tcLoading
      : mode === "requirement"
        ? reqLoading
        : mode === "cycle"
          ? cyclesLoading
          : versionId
            ? defectsLoading
            : projectDefectsLoading;

  // Priority id → name map (for the test-case PriorityBadge).
  const { data: prioritiesRes } = usePriorities(mode === "testcase");
  const priorityName = useMemo(() => {
    const raw =
      (prioritiesRes as any)?.data?.data ??
      (prioritiesRes as any)?.data ??
      (prioritiesRes as any)?.content ??
      prioritiesRes;
    const list: any[] = Array.isArray(raw) ? raw : [];
    const map = new Map<string, string>();
    list.forEach((p: any) => map.set(String(p.id), p.name));
    return (id?: number) => (id != null ? (map.get(String(id)) ?? null) : null);
  }, [prioritiesRes]);

  const pad = (id: number) => String(id).padStart(4, "0");

  const rows: EntityRow[] = useMemo(() => {
    if (mode === "testcase") {
      const list: any[] = (tcRes as any)?.data?.content ?? [];
      return list
        .filter((c) => Number(c.id) !== Number(excludeId))
        .map((c) => ({
          id: Number(c.id),
          name: c.name ?? "Untitled test case",
          code: `${cfg.codePrefix}-${pad(Number(c.id))}`,
          typeValue: c.scriptType ?? undefined,
        }));
    }
    if (mode === "cycle") {
      const list: CycleSummary[] = (cyclesRes as any)?.content ?? [];
      const q = search.trim().toLowerCase();
      // Collect the selected release folder + all its nested descendants so a
      // parent folder shows cycles from its child folders too.
      const allowedFolderIds = new Set<number>();
      if (selectedFolderId != null && selectedFolderId !== ALL_CYCLES_NODE_ID) {
        const collect = (nodes: TreeNode[], capturing: boolean) => {
          for (const n of nodes) {
            const isMatch = capturing || n.id === selectedFolderId;
            if (isMatch) allowedFolderIds.add(n.id);
            if (n.children?.length) collect(n.children, isMatch);
          }
        };
        collect(folderTree, false);
      }
      return list
        .filter((c) =>
          selectedFolderId == null || selectedFolderId === ALL_CYCLES_NODE_ID
            ? true
            : c.release_folder_id != null &&
              allowedFolderIds.has(Number(c.release_folder_id)),
        )
        .filter((c) => !q || (c.name ?? "").toLowerCase().includes(q))
        .filter((c) => {
          if (!testCaseType) return true;
          const cycleType = String(c.type ?? "").toUpperCase();
          const normalizedCycleType =
            cycleType === "AUTOMATED" ? "AUTOMATION" : cycleType;
          return normalizedCycleType === testCaseType;
        })
        .map((c) => ({
          id: Number(c.id),
          name: c.name ?? "Untitled cycle",
          code: `${cfg.codePrefix}-${pad(Number(c.id))}`,
          typeValue: c.type,
          statusValue: c.status,
          caseCount: c.total_cases,
        }));
    }
    if (mode === "defect") {
      const q = search.trim().toLowerCase();
      return defectsList
        .filter((d: any) => Number(d.id) !== Number(excludeId))
        .filter((d: any) => {
          const st = String(
            d.defect_status || d.defectStatus || "",
          ).toUpperCase();
          if (selectedFolderId === OPEN_DEFECTS_NODE_ID)
            return st === "OPEN" || st === "REOPENED";
          if (selectedFolderId === IN_PROGRESS_DEFECTS_NODE_ID)
            return st === "IN_PROGRESS";
          if (selectedFolderId === FIXED_DEFECTS_NODE_ID) return st === "FIXED";
          if (selectedFolderId === CLOSED_DEFECTS_NODE_ID)
            return st === "CLOSED";
          return true;
        })
        .filter((d: any) => {
          if (!q) return true;
          const title = (d.title || "").toLowerCase();
          const idStr = String(d.id);
          const keyStr = (d.defectKey || "").toLowerCase();
          return title.includes(q) || idStr.includes(q) || keyStr.includes(q);
        })
        .map((d: any) => ({
          id: Number(d.id),
          name: d.title ?? `Defect #${d.id}`,
          code: d.defectKey || `${cfg.codePrefix}-${pad(Number(d.id))}`,
          statusValue: d.defect_status || d.defectStatus || "OPEN",
          typeValue: d.severity || d.priority || undefined,
        }));
    }
    const list: any[] = (reqRes as any)?.data?.content ?? [];
    const q = search.trim().toLowerCase();
    return (
      list
        .filter((r) => Number(r.id) !== Number(excludeId))
        // Only Ready requirements can be linked — draft/unset are hidden.
        .filter(
          (r) =>
            String(
              r.status ?? r.approvalStatus ?? r.approval_status ?? "",
            ).toUpperCase() === "READY",
        )
        .filter(
          (r) =>
            !q ||
            (r.name ?? "").toLowerCase().includes(q) ||
            String(r.id).includes(q),
        )
        .map((r) => ({
          id: Number(r.id),
          name: r.name ?? "Untitled requirement",
          code: `${cfg.codePrefix}-${pad(Number(r.id))}`,
          typeValue: r.requirementtype ?? r.requirementType ?? undefined,
        }))
    );
  }, [
    mode,
    tcRes,
    reqRes,
    cyclesRes,
    defectsList,
    excludeId,
    cfg.codePrefix,
    search,
    selectedFolderId,
    folderTree,
    testCaseType,
  ]);

  // Keep a row focused for the preview pane.
  useEffect(() => {
    if (rows.length === 0) {
      setFocusedId(null);
    } else if (focusedId == null || !rows.some((r) => r.id === focusedId)) {
      setFocusedId(rows[0].id);
    }
  }, [rows, focusedId]);

  // ── Focused entity detail (preview pane) ──────────────────────
  const tcFocusId = mode === "testcase" ? (focusedId ?? 0) : 0;
  const { data: tcDetailRes, isLoading: tcDetailLoading } =
    useTestCaseDetailsQuery(tcFocusId);
  const { data: stepsRes, isLoading: stepsLoading } =
    useTestStepsQuery(tcFocusId);
  const steps: any[] = (stepsRes as any)?.data?.content ?? [];
  const { useWorkspaceVersions } = useAuth();
  const currentWorkspaceType =
    useWorkspaceVersions?.data?.workspace?.workspaceType ??
    useWorkspaceVersions?.data?.workspace?.type ??
    "WebApplication";
  const { data: nlaRes } = useNaturalTextActionsQuery({
    workspaceType: currentWorkspaceType,
  });
  const naturalTextActions: any[] =
    (nlaRes as any)?.data?.content ?? (nlaRes as any)?.data ?? [];

  const { data: reqDetailRes, isLoading: reqDetailLoading } =
    useRequirementById(mode === "requirement" ? (focusedId ?? 0) : 0, {
      enabled: mode === "requirement" && !!focusedId,
    });

  const { data: cycleDetailRes, isLoading: cycleDetailLoading } = useCycleById(
    mode === "cycle" ? (focusedId ?? null) : null,
  );

  const { data: defectDetailRes, isLoading: defectDetailLoading } =
    useDefectById(mode === "defect" ? (focusedId ?? 0) : 0);

  const detailLoading =
    mode === "testcase"
      ? tcDetailLoading
      : mode === "requirement"
        ? reqDetailLoading
        : mode === "cycle"
          ? cycleDetailLoading
          : defectDetailLoading;

  const focusedRow = rows.find((r) => r.id === focusedId) ?? null;
  const tcDetail = (tcDetailRes as any)?.data ?? (tcDetailRes as any) ?? null;
  const reqDetail =
    (reqDetailRes as any)?.data ?? (reqDetailRes as any) ?? null;
  const cycleDetail =
    (cycleDetailRes as any)?.data ?? (cycleDetailRes as any) ?? null;
  const defectDetail = (defectDetailRes as any) ?? null;

  const { data: teamRes } = useTeamMembers(open);
  const members: any[] =
    (teamRes as any)?.data?.content ??
    (Array.isArray(teamRes) ? (teamRes as any) : []);
  const createdByName = (id: any): string => {
    if (id == null) return "—";
    const u = members.find((m) => String(m.id) === String(id));
    if (!u) return "—";
    return (
      `${u.firstName ?? ""}${u.lastName ? ` ${u.lastName}` : ""}`.trim() ||
      u.name ||
      u.email ||
      "—"
    );
  };

  // ── selection ─────────────────────────────────────────────────
  // When an unlink handler is supplied, already-linked rows become editable:
  // they render checked by default and un-ticking marks them for removal.
  const unlinkEnabled = !!onUnlink;
  const isClosedCycleRow = (row: EntityRow) =>
    mode === "cycle" &&
    String(row.statusValue ?? "").toUpperCase() === "CLOSED";
  const isRowChecked = (id: number) => {
    const row = rows.find((r) => r.id === id);
    if (row && isClosedCycleRow(row)) return false;
    const hasSelectedSteps = (selectedStepsByTc.get(id)?.size ?? 0) > 0;
    if (hasSelectedSteps) return true;
    return alreadyLinked.has(id) ? !unlinkSet.has(id) : selected.has(id);
  };

  const selectableRows = (
    unlinkEnabled
      ? rows
      : rows.filter(
          (r) =>
            !alreadyLinked.has(r.id) ||
            (selectedStepsByTc.get(r.id)?.size ?? 0) > 0,
        )
  ).filter((r) => !isClosedCycleRow(r));
  const allShownSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => isRowChecked(r.id));

  const toLink = [...selected].filter((id) => {
    const hasSelectedSteps = (selectedStepsByTc.get(id)?.size ?? 0) > 0;
    return !alreadyLinked.has(id) || hasSelectedSteps;
  });
  const toUnlink = unlinkEnabled ? [...unlinkSet] : [];

  const totalSelectedSteps = Array.from(selectedStepsByTc.values()).reduce(
    (acc, steps) => acc + steps.size,
    0,
  );
  const wholeTcSelected = toLink.filter(
    (id) => !selectedStepsByTc.get(id) || selectedStepsByTc.get(id)!.size === 0,
  ).length;
  const totalItemsToLink =
    totalSelectedSteps > 0
      ? totalSelectedSteps + wholeTcSelected
      : toLink.length;

  if (!open) return null;

  const toggle = (id: number) => {
    const row = rows.find((r) => r.id === id);
    if (row && isClosedCycleRow(row)) return;
    if (alreadyLinked.has(id)) {
      if (!unlinkEnabled) return;
      setUnlinkSet((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // Clear step selections for this unselected test case
        setSelectedStepsByTc((stepMap) => {
          const m = new Map(stepMap);
          m.delete(id);
          return m;
        });
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleStepSelection = (
    tcId: number,
    stepId: number,
    stepName: string,
  ) => {
    setSelectedStepsByTc((prev) => {
      const next = new Map(prev);
      const tcSteps = new Map(next.get(tcId) ?? new Map());
      if (tcSteps.has(stepId)) {
        tcSteps.delete(stepId);
      } else {
        tcSteps.set(stepId, stepName);
      }
      if (tcSteps.size > 0) {
        next.set(tcId, tcSteps);
        // Ensure parent test case is checked
        setSelected((s) => new Set(s).add(tcId));
      } else {
        next.delete(tcId);
      }
      return next;
    });
  };

  const toggleStepUnlink = (tcId: number, stepId: number) => {
    const key = `${tcId}_${stepId}`;
    setUnlinkStepSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAllStepsForFocusedTc = () => {
    if (!focusedRow || steps.length === 0) return;
    const tcId = focusedRow.id;
    setSelectedStepsByTc((prev) => {
      const next = new Map(prev);
      const tcSteps = new Map<number, string>();
      steps.forEach((s: any, i: number) => {
        if (s.id != null) {
          tcSteps.set(Number(s.id), getStepLabel(s, i));
        }
      });
      next.set(tcId, tcSteps);
      return next;
    });
    setSelected((s) => new Set(s).add(tcId));
  };

  const clearStepsForFocusedTc = () => {
    if (!focusedRow) return;
    const tcId = focusedRow.id;
    setSelectedStepsByTc((prev) => {
      const next = new Map(prev);
      next.delete(tcId);
      return next;
    });
  };

  const selectAll = () => {
    if (allShownSelected) {
      // Uncheck everything visible: clear new selections + mark linked for unlink.
      setSelected((prev) => {
        const next = new Set(prev);
        selectableRows.forEach((r) => {
          if (!alreadyLinked.has(r.id)) next.delete(r.id);
        });
        return next;
      });
      setSelectedStepsByTc(new Map());
      if (unlinkEnabled)
        setUnlinkSet((prev) => {
          const next = new Set(prev);
          selectableRows.forEach((r) => {
            if (alreadyLinked.has(r.id)) next.add(r.id);
          });
          return next;
        });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        selectableRows.forEach((r) => {
          if (!alreadyLinked.has(r.id)) next.add(r.id);
        });
        return next;
      });
      if (unlinkEnabled)
        setUnlinkSet((prev) => {
          const next = new Set(prev);
          selectableRows.forEach((r) => {
            if (alreadyLinked.has(r.id)) next.delete(r.id);
          });
          return next;
        });
    }
  };

  const handleConfirm = async () => {
    if (
      totalItemsToLink === 0 &&
      toUnlink.length === 0 &&
      unlinkStepSet.size === 0
    ) {
      showToast({
        title: "Select at least one item to change",
        variant: "info",
      });
      return;
    }
    try {
      setSubmitting(true);
      if (toLink.length > 0) {
        const itemsToConfirm: any[] = [];
        toLink.forEach((tcId) => {
          const row = rows.find((r) => r.id === tcId);
          if (!row) return;
          const stepMap = selectedStepsByTc.get(tcId);
          if (stepMap && stepMap.size > 0) {
            stepMap.forEach((stepName, stepId) => {
              itemsToConfirm.push({
                id: row.id,
                testCaseId: row.id,
                name: row.name,
                testCaseName: row.name,
                type: row.typeValue,
                testCaseType: row.typeValue,
                testStepId: stepId,
                testStepName: stepName,
              });
            });
          } else if (!alreadyLinked.has(tcId)) {
            itemsToConfirm.push({
              id: row.id,
              testCaseId: row.id,
              name: row.name,
              testCaseName: row.name,
              type: row.typeValue,
              testCaseType: row.typeValue,
              testStepId: null,
              testStepName: null,
            });
          }
        });
        await onConfirm(toLink, itemsToConfirm);
      }
      if (toUnlink.length > 0 && onUnlink) await onUnlink(toUnlink);
      if (unlinkStepSet.size > 0 && (onUnlinkSteps || onUnlink)) {
        const stepLinkIds: number[] = [];
        unlinkStepSet.forEach((key) => {
          const [tcIdStr] = key.split("_");
          const tcId = Number(tcIdStr);
          // If parent test case is being unlinked entirely, skip individual steps to avoid redundant calls
          if (toUnlink.includes(tcId)) return;
          const linkId = alreadyLinkedStepLinkIds.get(key);
          if (linkId != null) {
            stepLinkIds.push(linkId);
          }
        });
        if (stepLinkIds.length > 0) {
          if (onUnlinkSteps) {
            await onUnlinkSteps(stepLinkIds);
          } else if (onUnlink) {
            await onUnlink(stepLinkIds);
          }
        }
      }
      setSelected(new Set());
      setUnlinkSet(new Set());
      setUnlinkStepSet(new Set());
      setSelectedStepsByTc(new Map());
      onClose();
    } catch {
      // Parent surfaces the error toast; keep the dialog open for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  const folderNameById = (id?: number): string | null => {
    if (id == null) return null;
    let found: string | null = null;
    const walk = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        if (n.id === id) found = n.title;
        else if (n.children) walk(n.children);
      }
    };
    walk(folderTree);
    return found;
  };

  // ── Folder tree renderer (matches the test-case page left tree:
  //    full-width rows, no radius, 12px font, depth*16+12 indent) ──────
  const renderTree = (nodes: TreeNode[], depth = 0) =>
    nodes.map((node) => {
      const collapsed = isNodeCollapsed(node.id);
      const nodeLoading = isNodeLoading(node.id);
      const active = selectedFolderId === node.id;
      return (
        <div key={node.id} className="mb-1">
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              setSelectedFolderId(node.id);
              if (node.hasChildren && collapsed) toggleCollapse(node.id, node);
            }}
            className={cn(
              "group/folder flex cursor-pointer items-center gap-1.5 py-1 text-[12px] font-medium text-foreground transition-colors",
              active ? "bg-muted" : "hover:bg-muted/60",
            )}
            style={{
              paddingLeft: `${depth * 16 + 12}px`,
              paddingRight: "12px",
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (node.hasChildren) toggleCollapse(node.id, node);
              }}
              className={cn(
                "grid h-3.5 w-3.5 shrink-0 place-items-center text-muted-foreground hover:text-foreground",
                !node.hasChildren && "invisible",
              )}
            >
              {nodeLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : collapsed ? (
                <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-left">
              {node.title}
            </span>
          </div>
          {!collapsed && node.children && node.children.length > 0 && (
            <div>{renderTree(node.children, depth + 1)}</div>
          )}
        </div>
      );
    });

  const folderTitle =
    selectedFolderId != null
      ? (folderNameById(selectedFolderId) ?? "Folder")
      : `All ${cfg.entityWord}s`;

  const Icon = cfg.icon;

  const treeLoading =
    mode === "cycle"
      ? releaseFoldersLoading
      : mode === "defect"
        ? false
        : foldersLoading;

  // Render the inline type badge for a list/preview row.
  const renderTypeBadge = (value?: string) => {
    if (!value) return null;
    if (mode === "testcase") {
      return <TestTypeBadge status={value} variant="lite" size="xs" />;
    }
    if (mode === "requirement") {
      return <ReqTypeBadge type={value} />;
    }
    if (mode === "defect") {
      return <SeverityBadge status={value} variant="lite" size="xs" />;
    }
    // Cycle type ("automated" | "manual") reuses the same automation/manual
    // badge styling as test cases.
    const typeKey =
      String(value).toLowerCase() === "automated" ? "AUTOMATION" : "MANUAL";
    return <TestTypeBadge status={typeKey} variant="lite" size="xs" />;
  };

  // Unified status badge — every mode renders the SAME badge style so the
  // preview/list header is consistent (type badge first, then status badge).
  const renderStatusBadge = (value?: string) => {
    if (!value) return null;
    if (mode === "cycle") {
      return <CycleStatusBadge status={value} variant="lite" size="xs" />;
    }
    if (mode === "requirement") {
      return <RequirementStatusBadge status={value} variant="lite" size="xs" />;
    }
    if (mode === "defect") {
      return <DefectStatusBadge status={value} variant="lite" size="xs" />;
    }
    return <TestCaseStatusBadge status={value} variant="lite" size="xs" />;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999999] grid place-items-center bg-black/40 p-6 pointer-events-auto"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex h-[85vh] max-h-[760px] w-full max-w-[1180px] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl pointer-events-auto"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-2.5">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-muted">
              <Icon className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-medium">{cfg.title}</h2>
                {mode === "cycle" && testCaseType && (
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold ${
                      testCaseType === "AUTOMATION"
                        ? "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-900"
                        : "bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:ring-purple-900"
                    }`}
                  >
                    {testCaseType === "AUTOMATION" ? (
                      <Zap className="h-4 w-4" strokeWidth={2} />
                    ) : (
                      <FileText className="h-4 w-4" strokeWidth={2} />
                    )}
                    <span>{testCaseType}</span>
                  </span>
                )}
              </div>
              <p className="text-[11.5px] text-muted-foreground">
                {cfg.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1 text-[11.5px] font-medium text-muted-foreground">
              {toUnlink.length > 0
                ? totalItemsToLink > 0
                  ? `${totalItemsToLink} to link · ${toUnlink.length} to unlink`
                  : `${toUnlink.length} to unlink`
                : totalSelectedSteps > 0
                  ? `${totalSelectedSteps} step${totalSelectedSteps > 1 ? "s" : ""}${wholeTcSelected > 0 ? ` + ${wholeTcSelected} case${wholeTcSelected > 1 ? "s" : ""}` : ""} selected`
                  : `${totalItemsToLink} selected`}
            </span>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body: 3 panes */}
        <div className="flex min-h-0 flex-1">
          {/* ── LEFT: Folders ── */}
          <div className="flex w-[250px] shrink-0 min-h-0 flex-col border-r border-border">
            <div className="shrink-0 px-4 pb-1 pt-3">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                Folders
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto py-1 scrollbar-thin">
              {treeLoading ? (
                <FolderSkeleton />
              ) : folderTree.length === 0 ? (
                <div className="px-3 py-8 text-center text-[12px] text-muted-foreground">
                  No folders found
                </div>
              ) : (
                renderTree(folderTree)
              )}
              {mode === "cycle" && isCreatingFolder && (
                <div className="flex items-center gap-1.5 px-3 py-1">
                  <span className="h-3.5 w-3.5" />
                  <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  {isSavingFolder ? (
                    <>
                      <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-muted-foreground">
                        {newFolderName}
                      </span>
                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                    </>
                  ) : (
                    <>
                      <input
                        ref={newFolderInputRef}
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveNewFolder();
                          if (e.key === "Escape") cancelCreateFolder();
                        }}
                        onBlur={() => {
                          if (!newFolderName.trim()) cancelCreateFolder();
                        }}
                        placeholder="New folder name…"
                        className="min-w-0 flex-1 border-b border-border bg-transparent py-0.5 text-[12px] font-medium outline-none placeholder:text-muted-foreground"
                      />
                      <button
                        onClick={saveNewFolder}
                        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        title="Save folder"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {mode === "cycle" && canCreateReleaseFolder && (
              <button
                onClick={startCreateFolder}
                className="flex shrink-0 items-center gap-2 border-t border-border px-4 py-2.5 text-[11.5px] font-medium text-foreground hover:bg-muted"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                New folder
              </button>
            )}
            {mode === "requirement" && (
              <div className="shrink-0 border-t border-border px-4 py-2.5">
                <p className="text-[10.5px] leading-snug text-muted-foreground">
                  Only{" "}
                  <span className="font-medium text-foreground">Ready</span>{" "}
                  requirements are shown. Change a requirement&apos;s status to
                  Ready to appear here.
                </p>
              </div>
            )}
          </div>

          {/* ── CENTER: Entities ── */}
          <div className="flex min-w-0 flex-1 min-h-0 flex-col">
            {mode === "cycle" &&
              contextEntities &&
              contextEntities.length > 0 && (
                <div className="shrink-0 border-b border-border bg-muted/30 px-5 py-2.5">
                  <div className="mb-1.5 flex items-center gap-1.5">
                    <ListChecks className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Adding {contextEntities.length} test case
                      {contextEntities.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto pr-1 custom-scrollbar">
                    {contextEntities.map((e) => (
                      <TestCaseTooltip key={e.id} tc={e}>
                        <span className="inline-flex max-w-64 items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground/80 cursor-default">
                          <span className="text-muted-foreground mr-1 shrink-0">
                            TC-{e.id}
                          </span>
                          <span className="truncate">{e.name}</span>
                        </span>
                      </TestCaseTooltip>
                    ))}
                  </div>
                  <div className="mt-2.5 flex items-center gap-1.5 text-[11px] font-medium text-warning">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span>
                      Only test cases with &quot;Ready&quot; status will be
                      added to the cycle.
                    </span>
                  </div>
                </div>
              )}
            <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-2.5">
              <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate text-[12.5px] font-medium text-foreground">
                {folderTitle}
              </span>
              <span className="text-muted-foreground/50">·</span>
              <span className="text-[12px] text-muted-foreground">
                {rows.length} {cfg.entityWord}
                {rows.length !== 1 ? "s" : ""}
              </span>
              {mode === "cycle" && canCreateCycle && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    startCreateCycle();
                  }}
                  className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:border-foreground/30 hover:text-foreground"
                >
                  <ClipboardList className="h-3 w-3" />
                  New cycle
                </button>
              )}
            </div>

            <div className="shrink-0 border-b border-border px-3 py-2.5">
              <div className="flex h-8 items-center gap-2 rounded-md bg-muted px-2.5 text-xs text-muted-foreground">
                <Search className="h-3.5 w-3.5" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={`Search ${cfg.entityWord}s…`}
                  className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>

            <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
              <label className="flex cursor-pointer items-center gap-2.5 text-[12px] font-medium text-foreground/80 hover:text-foreground">
                <Checkbox
                  checked={allShownSelected && selectableRows.length > 0}
                  onCheckedChange={selectAll}
                  disabled={selectableRows.length === 0}
                />
                Select all
              </label>
              <span className="text-[10.5px] text-muted-foreground">
                {selectableRows.length} available
              </span>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
              {selectedFolderId == null && folderTree.length > 0 && !listLoading ? (
                <div className="px-5 py-12 text-center text-[13px] text-muted-foreground">
                  Select a folder to view its {cfg.entityWord}s
                </div>
              ) : listLoading ? (
                <ListSkeleton />
              ) : rows.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-5 py-12 text-center text-[13px] text-muted-foreground">
                  <span>
                    {search
                      ? "No matching results"
                      : mode === "cycle"
                        ? "No cycles in this folder"
                        : "Nothing to link in this folder"}
                  </span>
                  {!search && mode === "cycle" && canCreateCycle && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        startCreateCycle();
                      }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-1.5 text-[12px] font-medium text-foreground hover:border-foreground/30"
                    >
                      <ClipboardList className="h-3.5 w-3.5" />
                      Create a cycle here
                    </button>
                  )}
                </div>
              ) : (
                rows.map((row) => {
                  const linked = alreadyLinked.has(row.id);
                  const markedUnlink = unlinkEnabled && unlinkSet.has(row.id);
                  const checked = isRowChecked(row.id);
                  const active = focusedId === row.id;
                  const isClosedCycle =
                    mode === "cycle" &&
                    String(row.statusValue ?? "").toUpperCase() === "CLOSED";
                  return (
                    <div
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setFocusedId(row.id)}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 border-b border-border px-3 py-1.5 transition-colors hover:bg-muted/40",
                        active && "bg-muted/60",
                        linked && !markedUnlink && "opacity-70",
                        isClosedCycle && "opacity-70",
                      )}
                    >
                      {!isClosedCycle && (
                        <Checkbox
                          checked={checked}
                          disabled={linked && !unlinkEnabled}
                          onCheckedChange={() => toggle(row.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {row.code}
                          </span>
                          {mode === "testcase" &&
                            renderTypeBadge(row.typeValue)}
                          {mode === "testcase" &&
                            (selectedStepsByTc.get(row.id)?.size ?? 0) > 0 && (
                              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                                {selectedStepsByTc.get(row.id)?.size}{" "}
                                {selectedStepsByTc.get(row.id)?.size === 1
                                  ? "step"
                                  : "steps"}
                              </span>
                            )}
                          {mode === "cycle" && renderTypeBadge(row.typeValue)}
                          {mode === "defect" && (
                            <>
                              {renderStatusBadge(row.statusValue)}
                              {renderTypeBadge(row.typeValue)}
                            </>
                          )}
                          {linked && markedUnlink ? (
                            <span className="text-[10px] font-medium text-destructive">
                              Will unlink
                            </span>
                          ) : linked ? (
                            <span className="text-[10px] font-medium text-emerald-600">
                              Linked
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-0.5 truncate text-[12px] text-foreground">
                          {row.name}
                        </div>
                      </div>
                      {mode === "cycle" && (
                        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                          {row.caseCount ?? 0} cases
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Resize handle + RIGHT: Preview (always visible) ── */}
          <div
            role="separator"
            aria-orientation="vertical"
            onMouseDown={() => {
              resizingRef.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
            }}
            className="relative w-0 shrink-0 cursor-col-resize"
          >
            <div className="absolute inset-y-0 -left-2 -right-2 z-10" />
          </div>

          <div
            className="flex min-w-0 shrink-0 min-h-0 flex-col border-l border-border"
            style={{ width: previewWidth }}
          >
            <div className="min-w-0 flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
              {!focusedRow ? (
                <div className="grid h-full place-items-center px-5 text-center text-[12.5px] text-muted-foreground">
                  Select{" "}
                  {mode === "testcase"
                    ? "a test case"
                    : mode === "cycle"
                      ? "a cycle"
                      : mode === "defect"
                        ? "a defect"
                        : "a requirement"}{" "}
                  to preview
                </div>
              ) : (
                <>
                  {/* Title + badges */}
                  <div className="border-b border-border px-5 py-4">
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {focusedRow.code}
                    </span>
                    <h3 className="mt-1 break-words text-[15px] font-semibold leading-snug tracking-tight">
                      {focusedRow.name}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {/* Consistent across all modes: TYPE badge first, then
                          STATUS badge (then priority for test cases). */}
                      {mode === "testcase" ? (
                        <>
                          {renderTypeBadge(focusedRow.typeValue)}
                          {renderStatusBadge(tcDetail?.status)}
                          {priorityName(tcDetail?.priorityId) && (
                            <PriorityBadge
                              status={priorityName(tcDetail?.priorityId)}
                              variant="lite"
                              size="xs"
                            />
                          )}
                        </>
                      ) : mode === "cycle" ? (
                        <>
                          {renderTypeBadge(focusedRow.typeValue)}
                          {renderStatusBadge(focusedRow.statusValue)}
                        </>
                      ) : mode === "defect" ? (
                        <>
                          {renderStatusBadge(
                            defectDetail?.defectStatus ?? focusedRow.statusValue,
                          )}
                          {renderTypeBadge(
                            defectDetail?.severity ?? focusedRow.typeValue,
                          )}
                          {defectDetail?.priority && (
                            <PriorityBadge
                              status={defectDetail.priority}
                              variant="lite"
                              size="xs"
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {renderTypeBadge(focusedRow.typeValue)}
                          {renderStatusBadge(reqDetail?.status)}
                        </>
                      )}
                    </div>
                  </div>

                  {detailLoading ? (
                    <DetailSkeleton />
                  ) : mode === "testcase" ? (
                    <>
                      {/* Description */}
                      <div className="border-b border-border px-5 py-4">
                        <SectionLabel icon={Info}>Description</SectionLabel>
                        <p className="[overflow-wrap:anywhere] break-words text-[12.5px] leading-relaxed text-foreground/90">
                          {stripHtml(tcDetail?.description) || (
                            <span className="text-muted-foreground">
                              No description
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Precondition */}
                      <div className="border-b border-border px-5 py-4">
                        <SectionLabel icon={ShieldCheck}>
                          Precondition
                        </SectionLabel>
                        <p className="[overflow-wrap:anywhere] break-words text-[12.5px] leading-relaxed text-foreground/90">
                          {stripHtml(tcDetail?.precondition) || (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </p>
                      </div>

                      {/* Steps */}
                      <div className="border-b border-border px-5 py-4">
                        <div className="mb-2 flex items-center justify-between">
                          <SectionLabel icon={ListChecks}>
                            Steps{" "}
                            {(selectedStepsByTc.get(tcFocusId)?.size ?? 0) > 0 && (
                              <span className="text-primary font-semibold lowercase">
                                ({selectedStepsByTc.get(tcFocusId)?.size} selected)
                              </span>
                            )}
                          </SectionLabel>
                          {steps.length > 0 && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={selectAllStepsForFocusedTc}
                                className="text-[11px] font-medium text-primary hover:underline cursor-pointer"
                              >
                                Select all
                              </button>
                              {(selectedStepsByTc.get(tcFocusId)?.size ?? 0) > 0 && (
                                <>
                                  <span className="text-muted-foreground/30 text-[10px]">·</span>
                                  <button
                                    type="button"
                                    onClick={clearStepsForFocusedTc}
                                    className="text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
                                  >
                                    Clear
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {stepsLoading ? (
                          <div className="space-y-2">
                            <Skeleton className="h-3 w-full rounded" />
                            <Skeleton className="h-3 w-5/6 rounded" />
                            <Skeleton className="h-3 w-2/3 rounded" />
                          </div>
                        ) : steps.length === 0 ? (
                          <div className="text-[12px] text-muted-foreground">
                            No steps defined
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <p className="text-[11px] text-muted-foreground">
                              Select specific steps to link, or leave unchecked to link the whole test case.
                            </p>
                            <ol className="space-y-1">
                              {steps.map((s: any, i: number) => {
                                const stepId = Number(s.id ?? i);
                                const isAlreadyLinked = !!alreadyLinkedStepsMap.get(tcFocusId)?.has(stepId);
                                const isChecked = isAlreadyLinked || !!selectedStepsByTc.get(tcFocusId)?.has(stepId);
                                const label = getStepLabel(s, i);

                                if (isAlreadyLinked) {
                                  const stepKey = `${tcFocusId}_${stepId}`;
                                  const isMarkedUnlink = unlinkStepSet.has(stepKey);
                                  const canUnlink = Boolean(onUnlinkSteps || unlinkEnabled);

                                  return (
                                    <li
                                      key={stepId}
                                      onClick={() => {
                                        if (canUnlink) toggleStepUnlink(tcFocusId, stepId);
                                      }}
                                      className={cn(
                                        "flex items-start gap-2.5 rounded-md px-2 py-1.5 text-[12px] transition-colors border",
                                        canUnlink ? "cursor-pointer" : "cursor-default",
                                        isMarkedUnlink
                                          ? "bg-destructive/5 border-destructive/20 opacity-80"
                                          : "bg-muted/30 border-transparent opacity-90 hover:bg-muted/50",
                                      )}
                                    >
                                      <Checkbox
                                        checked={!isMarkedUnlink}
                                        disabled={!canUnlink}
                                        onCheckedChange={() => {
                                          if (canUnlink) toggleStepUnlink(tcFocusId, stepId);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="mt-0.5 shrink-0"
                                      />
                                      <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded bg-muted text-[9.5px] font-semibold tabular-nums text-muted-foreground">
                                        {i + 1}
                                      </span>
                                      <span
                                        className={cn(
                                          "[overflow-wrap:anywhere] min-w-0 flex-1 break-words text-foreground/80",
                                          isMarkedUnlink && "line-through text-muted-foreground",
                                        )}
                                      >
                                        {String(
                                          focusedRow.typeValue ?? "",
                                        ).toUpperCase() === "MANUAL" ? (
                                          stripHtml(s.manualTestSteps) ||
                                          stripHtml(s.action) ||
                                          "—"
                                        ) : (
                                          <TestStepResultParser
                                            step={s}
                                            naturalTextActions={naturalTextActions}
                                          />
                                        )}
                                      </span>
                                      {isMarkedUnlink ? (
                                        <span className="text-[10px] font-medium text-destructive bg-destructive/10 border border-destructive/20 rounded px-1.5 py-0.5 shrink-0">
                                          Will unlink
                                        </span>
                                      ) : (
                                        <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded px-1.5 py-0.5 shrink-0">
                                          Linked
                                        </span>
                                      )}
                                    </li>
                                  );
                                }

                                return (
                                  <li
                                    key={stepId}
                                    onClick={() => toggleStepSelection(tcFocusId, stepId, label)}
                                    className={cn(
                                      "flex items-start gap-2.5 rounded-md px-2 py-1.5 text-[12px] cursor-pointer transition-colors border",
                                      isChecked
                                        ? "bg-primary/5 border-primary/30"
                                        : "hover:bg-muted/50 border-transparent",
                                    )}
                                  >
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={() => toggleStepSelection(tcFocusId, stepId, label)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="mt-0.5 shrink-0"
                                    />
                                    <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded bg-muted text-[9.5px] font-semibold tabular-nums text-muted-foreground">
                                      {i + 1}
                                    </span>
                                    <span className="[overflow-wrap:anywhere] min-w-0 flex-1 break-words text-foreground/90">
                                      {String(
                                        focusedRow.typeValue ?? "",
                                      ).toUpperCase() === "MANUAL" ? (
                                        stripHtml(s.manualTestSteps) ||
                                        stripHtml(s.action) ||
                                        "—"
                                      ) : (
                                        <TestStepResultParser
                                          step={s}
                                          naturalTextActions={naturalTextActions}
                                        />
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        )}
                      </div>

                      {/* Meta */}
                      <div className="px-5 py-4">
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <User className="h-3.5 w-3.5 shrink-0" /> Created by
                          </span>
                          <span className="truncate text-[12px] font-medium text-foreground">
                            {createdByName(tcDetail?.createdbyId)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <Folder className="h-3.5 w-3.5 shrink-0" /> Folder
                          </span>
                          <span className="truncate text-[12px] font-medium text-foreground">
                            {folderNameById(tcDetail?.testCaseFolderId) ?? "—"}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : mode === "cycle" ? (
                    <>
                      {/* Cycle: Description */}
                      <div className="border-b border-border px-5 py-4">
                        <SectionLabel icon={Info}>Description</SectionLabel>
                        <p className="[overflow-wrap:anywhere] break-words text-[12.5px] leading-relaxed text-foreground/90">
                          {cycleDetail?.description?.trim() ? (
                            cycleDetail.description
                          ) : (
                            <span className="text-muted-foreground">
                              No description
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Cycle: Results breakdown */}
                      <div className="border-b border-border px-5 py-4">
                        <SectionLabel icon={Activity}>Results</SectionLabel>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            {
                              label: "Passed",
                              value: cycleDetail?.pass_count,
                              tone: "text-success",
                            },
                            {
                              label: "Failed",
                              value: cycleDetail?.fail_count,
                              tone: "text-destructive",
                            },
                            {
                              label: "Blocked",
                              value: cycleDetail?.blocked_count,
                              tone: "text-warning",
                            },
                            {
                              label: "Untested",
                              value: cycleDetail?.untested_count,
                              tone: "text-muted-foreground",
                            },
                          ].map((m) => (
                            <div
                              key={m.label}
                              className="rounded-md bg-muted/50 px-2.5 py-2"
                            >
                              <div
                                className={cn(
                                  "text-[15px] font-semibold tabular-nums",
                                  m.tone,
                                )}
                              >
                                {m.value ?? 0}
                              </div>
                              <div className="text-[11px] text-muted-foreground">
                                {m.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Cycle: Meta */}
                      <div className="px-5 py-4">
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <ListChecks className="h-3.5 w-3.5 shrink-0" />{" "}
                            Total cases
                          </span>
                          <span className="text-[12px] font-medium text-foreground">
                            {cycleDetail?.total_cases ??
                              focusedRow.caseCount ??
                              0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <CalendarDays className="h-3.5 w-3.5 shrink-0" />{" "}
                            Start date
                          </span>
                          <span className="text-[12px] font-medium text-foreground">
                            {cycleDetail?.start_date
                              ? new Date(
                                  cycleDetail.start_date,
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <CalendarDays className="h-3.5 w-3.5 shrink-0" />{" "}
                            End date
                          </span>
                          <span className="text-[12px] font-medium text-foreground">
                            {cycleDetail?.end_date
                              ? new Date(
                                  cycleDetail.end_date,
                                ).toLocaleDateString()
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : mode === "defect" ? (
                    <>
                      {/* Defect: Description */}
                      <div className="border-b border-border px-5 py-4">
                        <SectionLabel icon={Info}>Description</SectionLabel>
                        <p className="[overflow-wrap:anywhere] break-words text-[12.5px] leading-relaxed text-foreground/90">
                          {stripHtml(defectDetail?.description) || (
                            <span className="text-muted-foreground">
                              No description
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Defect: Steps to reproduce */}
                      {defectDetail?.stepsToReproduce && (
                        <div className="border-b border-border px-5 py-4">
                          <SectionLabel icon={ListChecks}>
                            Steps to reproduce
                          </SectionLabel>
                          <p className="[overflow-wrap:anywhere] whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-foreground/90">
                            {stripHtml(defectDetail.stepsToReproduce)}
                          </p>
                        </div>
                      )}

                      {/* Defect: Expected / Actual Results */}
                      {(defectDetail?.expectedResults ||
                        defectDetail?.actualResults) && (
                        <div className="space-y-3 border-b border-border px-5 py-4">
                          {defectDetail?.expectedResults && (
                            <div>
                              <span className="text-[11px] font-semibold text-muted-foreground">
                                Expected Result
                              </span>
                              <p className="mt-0.5 text-[12px] text-foreground/90">
                                {stripHtml(defectDetail.expectedResults)}
                              </p>
                            </div>
                          )}
                          {defectDetail?.actualResults && (
                            <div>
                              <span className="text-[11px] font-semibold text-destructive">
                                Actual Result
                              </span>
                              <p className="mt-0.5 text-[12px] text-foreground/90">
                                {stripHtml(defectDetail.actualResults)}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Defect: Meta */}
                      <div className="space-y-2 px-5 py-4">
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <User className="h-3.5 w-3.5 shrink-0" /> Assignee
                          </span>
                          <span className="truncate text-[12px] font-medium text-foreground">
                            {defectDetail?.assigneeName || "Unassigned"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <User className="h-3.5 w-3.5 shrink-0" /> Reporter
                          </span>
                          <span className="truncate text-[12px] font-medium text-foreground">
                            {defectDetail?.reporterName ||
                              createdByName(defectDetail?.createdBy) ||
                              "—"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <CalendarDays className="h-3.5 w-3.5 shrink-0" /> Created
                          </span>
                          <span className="text-[12px] font-medium text-foreground">
                            {defectDetail?.createdDate
                              ? formatShortDateTime(defectDetail.createdDate)
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Requirement: Description */}
                      <div className="border-b border-border px-5 py-4">
                        <SectionLabel icon={Info}>Description</SectionLabel>
                        {(() => {
                          const raw =
                            reqDetail?.detailedRequirements ||
                            reqDetail?.description ||
                            "";
                          if (!raw.trim())
                            return (
                              <span className="text-[12px] text-muted-foreground">
                                No description
                              </span>
                            );
                          const html = DOMPurify.sanitize(
                            marked.parse(raw, { async: false }) as string,
                          );
                          return (
                            <div
                              className="rich-text max-w-none text-[12.5px] leading-relaxed text-foreground/90 [overflow-wrap:anywhere] break-words"
                              dangerouslySetInnerHTML={{ __html: html }}
                            />
                          );
                        })()}
                      </div>

                      {/* Requirement: Meta */}
                      <div className="px-5 py-4">
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <User className="h-3.5 w-3.5 shrink-0" /> Created by
                          </span>
                          <span className="truncate text-[12px] font-medium text-foreground">
                            {createdByName(
                              reqDetail?.userid ??
                                reqDetail?.createdBy ??
                                reqDetail?.userId,
                            )}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 py-1">
                          <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                            <Activity className="h-3.5 w-3.5 shrink-0" /> Linked
                            tests
                          </span>
                          <span className="text-[12px] font-medium text-foreground">
                            {reqDetail?.automationcount ??
                              reqDetail?.automationCount ??
                              0}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {mode === "cycle" && (
          <CreateCycleDialog
            open={isCreateCycleDialogOpen}
            onClose={() => setIsCreateCycleDialogOpen(false)}
            onCreated={handleCycleCreated}
            defaultType={testCaseType === "MANUAL" ? "manual" : "automated"}
            defaultFolderId={
              selectedFolderId === ALL_CYCLES_NODE_ID ? null : selectedFolderId
            }
            versionId={versionId}
            selectedTestCaseType={testCaseType}
          />
        )}

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border px-6 py-2.5">
          <button
            onClick={onClose}
            className="h-8 rounded-md px-4 text-[12.5px] font-medium text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={
              submitting ||
              (totalItemsToLink === 0 &&
                toUnlink.length === 0 &&
                unlinkStepSet.size === 0)
            }
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-4 text-[12.5px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : mode === "cycle" ? (
              <ClipboardList className="h-3.5 w-3.5" />
            ) : mode === "defect" ? (
              <Bug className="h-3.5 w-3.5" />
            ) : (
              <Link2 className="h-3.5 w-3.5" />
            )}
            {submitting
              ? "Saving…"
              : toLink.length === 0 &&
                  toUnlink.length === 0 &&
                  unlinkStepSet.size > 0
                ? `Unlink ${unlinkStepSet.size} step${unlinkStepSet.size > 1 ? "s" : ""}`
                : toLink.length === 0 &&
                    toUnlink.length > 0 &&
                    unlinkStepSet.size === 0
                  ? `Unlink ${toUnlink.length} ${cfg.entityWord}${toUnlink.length > 1 ? "s" : ""}`
                  : (totalItemsToLink > 0 &&
                        (toUnlink.length > 0 || unlinkStepSet.size > 0)) ||
                      (toUnlink.length > 0 && unlinkStepSet.size > 0)
                    ? "Apply changes"
                    : `${cfg.actionVerb}${totalItemsToLink > 0 ? ` ${totalItemsToLink} ${totalSelectedSteps > 0 ? (totalItemsToLink === 1 ? "item" : "items") : `${cfg.entityWord}${totalItemsToLink > 1 ? "s" : ""}`}` : ""}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default EntityLinkDialog;
